import { isEqual } from "lodash";
import {  CommData, ParameterData, SheetMapping, TrackerChangeEntry, TrackerData, TrackerDelta, UploadData } from "./types";
import { SheetReader, SheetView } from "./sheet_reader";
import { convertNames } from "./convert_names";
import { serialToAddress, trackerArrayToMap } from "../common/util";

const parseNumber = (data: string|number|null): number|null => {
  if(typeof data === 'string') {
    data = parseFloat(data);

    if(isNaN(data)) {
      return null;
    }

    return data;
  } else {
    return data;
  }
};

const parsers = new Map(Object.entries({
  cleaning: parseNumber,
  lat: parseNumber,
  long: parseNumber,
  elevation: parseNumber,
  calibration: parseNumber,
}));

const parseRow = (raw: Record<string,string|number|null>): Record<string,string|number|null> => {
  const entries = Object.entries(raw).map(([key, value]): [string, string|number|null] => {
    const parser = parsers.get(key);

    if(parser != null) {
      value = parser(value);
    }

    return [key, value];
  });

  return Object.fromEntries(entries);
};

export const loadSheetDeltas = (sheet: SheetView, mapping: SheetMapping): Map<string,TrackerDelta> => {
  const rows = Array.from(sheet.getData()).map((line) => {
    return convertNames(line, mapping);
  });

  const entries = rows.map((row): [string, TrackerDelta] | undefined => {
    if(row.id == null) {
      return undefined;
    }

    const deltaEntries = Object.entries(row)
      .filter(([key, value]) => {
        return key !== 'id' && value != null;
      }).map(([key, value]): [string, string|number|null] => {
        return [key, value === '-' ? null : value];
      });

    return [String(row.id), new Map(deltaEntries)];
  }).filter((entry): entry is [string, TrackerDelta] => {
    return entry != null;
  });

  return new Map(entries);
}

export const loadParameter = (sheet: SheetView | undefined, mapping: SheetMapping | undefined): ParameterData | undefined => {
  if(sheet == undefined || mapping == undefined || mapping.cells == undefined) {
    return undefined;
  }

  const data: ParameterData = {};
  for(const cell of mapping.cells) {
    const offset = cell.col_offset ?? 1;
    const value = sheet.getCell(cell.col + offset, cell.row);
    if(cell.mapValue) {
      data[cell.key] = cell.mapValue(value);
    } else {
      data[cell.key] = value;
    }
  }

  return data;
}

export const loadCommunication = (sheet: SheetView | undefined, mapping: SheetMapping | undefined): CommData[] | undefined => {
  if(sheet == undefined || mapping == undefined || mapping.columns == undefined) {
    return undefined;
  }

  const rows = Array.from(sheet.getData()).map((line) => {
    return convertNames(line, mapping);
  }).filter( (line): line is CommData => line['id'] != null && line['id'] != '-' );

  return rows;
}

export const loadSheet = (file: File) => {
  return new Promise<SheetReader>((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = async (loadEvent) => {
      try {
        const data = fileReader.result;
        const sheetReader = new SheetReader(data, file.name);
        resolve(sheetReader);
      } catch {
        reject(new Error("Unable to parse file"));
      }
    };

    fileReader.onerror = () => {
      reject(new Error("Unable to read file"));
    };

    fileReader.readAsBinaryString(file);
  });
};

export const getChangeStats = (changes: TrackerChangeEntry[], prev: TrackerData[], next: TrackerData[]) => {
  let updates = 0;
  let inserts = 0;
  let deletes = 0;
  let unchanged = 0;

  const prevMap = trackerArrayToMap(prev);
  const nextMap = trackerArrayToMap(next);

  for(const change of changes) {
    if(change.delta == null) {
      deletes += 1;
    } else if(change.tracker == null) {
      inserts += 1;
    } else {
      updates += 1;

      if(isEqual(prevMap.get(change.id), nextMap.get(change.id))) {
        unchanged += 1;
      }
    }
  }

  return { updates, inserts, deletes, unchanged };
}

